/**
 * HEIC / HEIF → an image this browser can draw. The ONE copy, shared by every
 * uploader: the form builder (firebase-image-upload.js), the campaign wizard
 * (bulk-sharing-wizard.js), the wedding builder (wedding-builder.js) and the
 * seat-map uploaders (landing-page-builder.js, event-site.js).
 *
 * Every uploader advertises .heic/.heif, but only Safari can draw HEIC into a
 * canvas — Chrome, Edge and Firefox, desktop AND Android, cannot. So a
 * non-Safari browser converts the file to JPEG here, in the browser.
 *
 * The decoder is heic-to (maintained, tracks libheif releases). It replaced
 * heic2any@0.0.4, whose 2020-era libheif rejects every photo taken on iOS 18 or
 * later with "ERR_LIBHEIF format not supported" (heic2any#61, libheif#1190) —
 * a French host's iPhone photo failed that way on /fr/builder 2026-10-26.
 * Bump the pinned version below when libheif ships a decode fix.
 *
 * Usage: `await window.qrHeic.toDecodableImage(file, { onConverting })` —
 * resolves to the same file when no conversion is needed, else to a .jpg File.
 */
(function () {
    'use strict';
    if (window.qrHeic) return;

    const CONVERTER_URL = 'https://cdn.jsdelivr.net/npm/heic-to@1.6.5/dist/iife/heic-to.js';
    let converterPromise = null;

    /**
     * @param {File} file
     * @returns {boolean} Whether the name or MIME type says HEIC/HEIF
     */
    function looksHeic(file) {
        const name = String((file && file.name) || '').toLowerCase();
        return /image\/hei[cf]/i.test((file && file.type) || '') || /\.hei[cf]$/.test(name);
    }

    /**
     * @param {Blob} blob - Candidate image bytes
     * @returns {Promise<boolean>} Whether this browser can decode them unaided
     */
    function canDecode(blob) {
        if (typeof createImageBitmap !== 'function') return Promise.resolve(false);
        return createImageBitmap(blob).then(
            (bmp) => { if (bmp.close) bmp.close(); return true; },
            () => false
        );
    }

    /**
     * Lazily fetch the converter (~700KB compressed), and only for a file this
     * browser has already been proven unable to read, so Safari never pays it.
     * @returns {Promise<Function>} heic-to's entry point
     */
    function loadConverter() {
        if (typeof window.HeicTo === 'function') return Promise.resolve(window.HeicTo);
        if (converterPromise) return converterPromise;
        converterPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = CONVERTER_URL;
            script.onload = () => typeof window.HeicTo === 'function'
                ? resolve(window.HeicTo)
                : reject(new Error('Could not load the iPhone-photo converter. Please save the photo as JPG and try again.'));
            script.onerror = () => {
                converterPromise = null; // let a later attempt retry the fetch
                reject(new Error('Could not load the iPhone-photo converter. Check your connection, or save the photo as JPG and try again.'));
            };
            document.head.appendChild(script);
        });
        return converterPromise;
    }

    /**
     * Return a file this browser can actually draw. Non-HEIC files pass straight
     * through, and so does HEIC on Safari — neither pays the download or the
     * multi-second decode.
     * @param {File} file - The host's chosen file
     * @param {{onConverting?: Function}} [opts] - Called once, just before a
     *   conversion starts (it takes seconds, so the caller should say so)
     * @returns {Promise<File>} A drawable file
     */
    async function toDecodableImage(file, opts) {
        if (!looksHeic(file)) return file;
        if (await canDecode(file)) return file;

        if (opts && typeof opts.onConverting === 'function') opts.onConverting();
        const heicTo = await loadConverter();
        let converted;
        try {
            converted = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.92 });
        } catch (cause) {
            // Logged so error-buffer.js reports libheif's own reason — the
            // message the host sees below cannot carry it.
            console.error('HEIC conversion failed:', cause && cause.message ? cause.message : cause);
            converted = null;
        }
        if (!converted || !converted.size) {
            throw new Error('This iPhone photo could not be converted. Please save it as JPG and try again.');
        }
        const baseName = (file.name || 'image').replace(/\.[^.]+$/, '') || 'image';
        return new File([converted], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
    }

    window.qrHeic = { looksHeic, toDecodableImage };
})();
